import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { logger } from '../utils/logger';
import { getSubscription } from '../utils/subscriptionApi';
import { computeManageableCatalogs } from '../utils/manageableCatalogs';

export interface DelegatedCatalogInfo {
  delegatorUserId: string;
  delegatorAliId: string;
  /** Display name of the catalog owner (delegator). Optional for backwards compatibility. */
  delegatorName?: string;
  collectionId: string;
  permissions: 'view' | 'full';
  /**
   * Granular CV-edit capability, decoupled from view/full item access
   * (migration 0035). Optional for backwards compatibility with older
   * API responses that predate the field.
   */
  canEditCv?: boolean;
  expiresAt: string;
}

/**
 * One of the user's owned catalogs (primary or add-on), surfaced for the
 * switcher dropdown when ``enableCatalogSwitching`` is on. Fetched from
 * ``GET /api/subscription`` so the source of truth is the subscription
 * service's ``user_catalogs`` view.
 */
export interface OwnedCatalogInfo {
  catalogAliId: string;
  creatorName: string;
  isPrimary: boolean;
}

/**
 * Unified switcher-row shape (PR 4b). When ``enableCatalogSwitching`` is on,
 * the switcher renders one flat list of these — owned (primary + add-ons) +
 * delegated — distinguished only by the ``kind`` badge. Selection sets
 * ``activeCatalogAliId`` to ``catalogAliId``; the legacy
 * ``activeCatalogOwnerId`` only flips when a *delegated* entry is selected,
 * preserving behaviour for existing consumers.
 */
export interface AvailableCatalogInfo {
  catalogAliId: string;
  /** Human-readable label, e.g. "Estate of Anna Hartley" or a delegator name. */
  label: string;
  kind: 'own-primary' | 'own-addon' | 'delegated';
  /** Owner userId — same as the caller's for own catalogs, the delegator's for delegated. */
  ownerUserId: string | null;
  /** Only set when ``kind === 'delegated'``. */
  delegation?: DelegatedCatalogInfo;
}

/**
 * A catalog the user may CREATE/MANAGE albums in: one of their own catalogs, or
 * an owner they hold an active, non-expired ``full`` delegation from. Albums are
 * owner-keyed, so a user's own catalogs collapse into a single entry
 * (``ownerUserId === null``) and each delegated owner appears once.
 * ``catalogAliId`` is the LL-NXX to pass to ``switchToCatalogAliId`` to make the
 * catalog active (``null`` = the own primary).
 */
export interface ManageableCatalogInfo {
  catalogAliId: string | null;
  label: string;
  ownerUserId: string | null;
  kind: 'own' | 'delegated';
}

export interface ActiveCatalogContextType {
  // ---------------------------------------------------------------------
  // Pre-PR-4b fields — preserved verbatim so existing consumers (Search,
  // Collections, FieldFavorites, GettyFavorites, …) continue to work
  // without modification. ``activeCatalogOwnerId`` is still the delegator
  // userId when a delegated catalog is selected, null otherwise.
  // ---------------------------------------------------------------------
  activeCatalogOwnerId: string | null;
  activeDelegation: DelegatedCatalogInfo | null;
  /**
   * True iff the caller holds an active (non-expired) delegation with
   * ``canEditCv`` from the *currently active* delegated owner. Computed
   * across ALL of that owner's delegation rows (per-collection grain
   * means one owner→delegate pair can have several), because CV access
   * is owner-scoped, not collection-scoped — reading the flag off a
   * single ``activeDelegation`` row would be wrong when rows disagree.
   * Always false when not viewing a delegated catalog.
   */
  activeOwnerCanEditCv: boolean;
  delegatedCatalogs: DelegatedCatalogInfo[];
  isLoadingDelegations: boolean;
  /**
   * True once the delegations fetch has settled at least once (success or
   * fail). Distinct from `!isLoadingDelegations`, which is also true in the
   * brief window before the fetch starts. Consumers that must not act on a
   * null/empty `activeDelegation` until delegations are known (e.g. the
   * DescribeTab owner-creator guard) should gate on this latch.
   */
  delegationsFetched: boolean;
  isViewingDelegatedCatalog: boolean;
  switchCatalog: (ownerId: string | null) => void;
  switchToOwnCatalog: () => void;
  refreshDelegations: () => Promise<void>;
  /**
   * Returns true if the caller has an active, non-expired delegation from
   * ``delegatorUserId``. Used by search-result and bookmark link builders to
   * choose the authenticated detail route for delegated items. Always false
   * before delegations have loaded (callers should treat that as "fall back
   * to public route", which is the safe default).
   */
  isDelegateOf: (delegatorUserId: string) => boolean;

  // ---------------------------------------------------------------------
  // PR 4b additions — multi-catalog ownership. Only meaningful when
  // ``enableCatalogSwitching`` is on; otherwise ownedCatalogs is empty,
  // activeCatalogAliId tracks the primary's ALI ID (or null), and
  // availableCatalogs only contains delegations (matching the legacy shape).
  // ---------------------------------------------------------------------
  /** LL-NXX of the active catalog. null = primary OR delegated catalog. */
  activeCatalogAliId: string | null;
  /** Owned catalogs from /api/subscription (primary first, then add-ons). */
  ownedCatalogs: OwnedCatalogInfo[];
  /** Unified list of every selectable catalog (owned + delegated). */
  availableCatalogs: AvailableCatalogInfo[];
  /** True while owned catalogs are being fetched from /api/subscription. */
  isLoadingOwnedCatalogs: boolean;
  /**
   * GH#1254: true when the user owns at least one catalog and EVERY one of
   * them is expired — the account is in viewer mode until reactivation.
   * Expired catalogs are excluded from ``ownedCatalogs`` (an expired catalog
   * is fully hidden, even from its owner); this flag is how the deactivated
   * banner knows the difference between "no catalogs" and "all expired".
   */
  accountExpired: boolean;
  /** Select a catalog by its LL-NXX. Updates legacy fields if it's a delegation. */
  switchToCatalogAliId: (catalogAliId: string | null) => void;
  /**
   * Catalogs the user may create/manage albums in: their own catalog(s) plus
   * every owner they hold an active, non-expired ``full`` delegation from
   * (deduped by owner). Empty when the user owns nothing and holds no full
   * delegation. The single source of truth for "can I manage albums here?" —
   * the Albums page gate and the Create Album dialog both read this.
   */
  manageableCatalogs: ManageableCatalogInfo[];
}

/**
 * Merges multiple delegation rows for the SAME delegator into one. The
 * delegations PK is per-collection (migration 0007) and per-catalog
 * (migration 0052) grain, so one owner can legitimately produce several rows
 * for the same delegate (e.g. two album-scoped grants) — these must not be
 * treated as independent delegators. Merge rule: the highest permission tier
 * wins (`full` beats `view`), and `canEditCv` is OR-ed across all rows.
 * Returns null when given an empty array.
 */
function mergeDelegationRows(rows: DelegatedCatalogInfo[]): DelegatedCatalogInfo | null {
  if (rows.length === 0) return null;
  const base = rows.find((d) => d.permissions === 'full') ?? rows[0];
  const canEditCv = rows.some((d) => d.canEditCv);
  return { ...base, canEditCv };
}

/**
 * Resolves the merged delegation for a delegator identified by their
 * catalog ALI ID (the key used by the switcher / ``switchToCatalogAliId``).
 */
function resolveBestDelegationByAliId(
  delegatedCatalogs: DelegatedCatalogInfo[],
  delegatorAliId: string
): DelegatedCatalogInfo | null {
  return mergeDelegationRows(delegatedCatalogs.filter((d) => d.delegatorAliId === delegatorAliId));
}

/**
 * Resolves the merged delegation for a delegator identified by their
 * legacy userId (the key used by ``activeCatalogOwnerId``).
 */
function resolveBestDelegationByUserId(
  delegatedCatalogs: DelegatedCatalogInfo[],
  delegatorUserId: string
): DelegatedCatalogInfo | null {
  return mergeDelegationRows(delegatedCatalogs.filter((d) => d.delegatorUserId === delegatorUserId));
}

export const ActiveCatalogContext = createContext<ActiveCatalogContextType | undefined>(undefined);

// Legacy localStorage key (pre-PR-4b). Holds userId of the active delegator
// or the OWN_CATALOG_SENTINEL. Migration to the new key happens on first load
// when enableCatalogSwitching is on.
const STORAGE_KEY = 'activeCatalogOwnerId';
// New key (PR 4b) — holds the active catalog's LL-NXX, or empty string for
// "primary" (we use empty rather than missing so we can distinguish
// "user explicitly chose primary" from "first-ever visit").
const ALI_STORAGE_KEY = 'activeCatalogAliId';
const PRIMARY_SENTINEL = '__primary__';

/**
 * Sentinel value stored in localStorage to distinguish "user explicitly chose
 * their own catalog" from "first-ever visit (no prior choice)."
 * Never exposed via the context API — React state uses null for own catalog.
 */
const OWN_CATALOG_SENTINEL = '__own__';

/**
 * Roles that own a catalog. Users with these roles should default to their own catalog.
 *
 * Includes both the normalised PermissionsContext format ('catalog-owner') AND the raw
 * Cognito custom:userRole attribute values ('catalog_owner', 'commercial') because
 * useAuth().user.role returns the raw attribute, not the normalised form.
 */
const CATALOG_OWNER_ROLES = new Set([
  'catalog-owner', 'admin', 'legacy-manager',
  'catalog_owner', 'commercial',
]);

export const ActiveCatalogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [activeCatalogOwnerId, setActiveCatalogOwnerId] = useState<string | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === OWN_CATALOG_SENTINEL || saved === null) return null;
    return saved;
  });
  const [delegatedCatalogs, setDelegatedCatalogs] = useState<DelegatedCatalogInfo[]>([]);
  const [isLoadingDelegations, setIsLoadingDelegations] = useState(false);
  const isInitialized = useRef(false);
  // Tracks whether each fetch has run at least once (success or fail). The
  // reconciliation effect uses these to avoid clobbering the saved ALI on
  // first render, before fetches have populated availableCatalogs. State (not
  // refs) so the effect re-runs once they flip true.
  const [delegationsFetched, setDelegationsFetched] = useState(false);
  const [ownedCatalogsFetched, setOwnedCatalogsFetched] = useState(false);

  // PR 4b — owned catalogs (primary + add-ons), fetched from /api/subscription
  // (with an /api/user/ali-id fallback) for every authenticated user. The
  // ``enableCatalogSwitching`` flag is now vestigial (runtime gating removed —
  // see featureFlags.ts), so this fetch runs unconditionally and is the source
  // of the own entry in ``manageableCatalogs`` (the album-management gate).
  const [ownedCatalogs, setOwnedCatalogs] = useState<OwnedCatalogInfo[]>([]);
  const [isLoadingOwnedCatalogs, setIsLoadingOwnedCatalogs] = useState(false);
  const [accountExpired, setAccountExpired] = useState(false);
  const [activeCatalogAliId, setActiveCatalogAliId] = useState<string | null>(() => {
    const saved = localStorage.getItem(ALI_STORAGE_KEY);
    if (saved === PRIMARY_SENTINEL || saved === null) return null;
    return saved;
  });

  const fetchDelegations = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoadingDelegations(true);
    try {
      const response = await api.get('/api/delegations');
      const delegations = response.data.delegations || [];

      const mapped: DelegatedCatalogInfo[] = delegations.map(
        (d: { delegatorUserId: string; delegatorAliId: string; delegatorName?: string; collectionId: string; permissions: string; canEditCv?: boolean; expiresAt: string }) => ({
          delegatorUserId: d.delegatorUserId,
          delegatorAliId: d.delegatorAliId,
          delegatorName: d.delegatorName,
          collectionId: d.collectionId,
          permissions: d.permissions as 'view' | 'full',
          canEditCv: d.canEditCv ?? false,
          expiresAt: d.expiresAt,
        })
      );

      setDelegatedCatalogs(mapped);

      // Initialize active catalog on first load.
      // Three-way branch on localStorage value:
      //   - '__own__' sentinel → user explicitly chose own catalog, respect it
      //   - delegation ID      → validate against API, fall back if stale
      //   - absent (null)      → first-ever visit, use role-based defaults
      if (!isInitialized.current) {
        // Role not yet available — defer initialization for EVERY branch.
        // fetchDelegations depends on [isAuthenticated, user?.role], so when
        // user.role populates the callback is recreated and the effect
        // re-fires. We must not finalize a selection (or worse, write the
        // __own__ sentinel) while we still can't tell an owner from a
        // delegate — that's what stranded pure delegates on a non-existent
        // own catalog. isInitialized stays false so this re-runs.
        if (user?.role === undefined) {
          return;
        }

        const savedOwnerId = localStorage.getItem(STORAGE_KEY);
        const isCatalogOwner = CATALOG_OWNER_ROLES.has(user.role);
        // A non-owner-role user with at least one delegation should be routed
        // to a delegated catalog, never left on the __own__ state — for them
        // it's an empty grid with no switcher to escape (the header switcher
        // hides at <=1 catalog). This keys off ROLE, not a count of owned
        // catalogs: ownedCatalogs is also empty when the switcher flag is off,
        // so it can't distinguish a real owner from a pure delegate. Trade-off:
        // a catalog-owner-ROLE user (e.g. 'commercial') who happens to own no
        // catalog is treated as an owner and is out of scope for this recovery.
        const nonOwnerWithDelegation = !isCatalogOwner && mapped.length > 0;

        // Auto-select the first delegation as the active catalog. Also seeds
        // the new ALI key — fetchOwnedCatalogs leaves it unset for users with
        // no owned catalogs. Guarded by `=== null` so an existing explicit ALI
        // choice isn't clobbered on refresh.
        const selectFirstDelegation = () => {
          const first = mapped[0];
          setActiveCatalogOwnerId(first.delegatorUserId);
          localStorage.setItem(STORAGE_KEY, first.delegatorUserId);
          if (localStorage.getItem(ALI_STORAGE_KEY) === null) {
            setActiveCatalogAliId(first.delegatorAliId);
            localStorage.setItem(ALI_STORAGE_KEY, first.delegatorAliId);
          }
        };

        if (savedOwnerId === OWN_CATALOG_SENTINEL) {
          if (nonOwnerWithDelegation) {
            // Stranded-on-own recovery: a non-owner can't legitimately rest on
            // their own catalog. Re-resolve to the first delegation so the
            // delegate can see the shared catalog again.
            selectFirstDelegation();
          }
          // else: a real catalog owner explicitly chose own — respect it.
          // activeCatalogOwnerId is already null from useState init.
        } else if (savedOwnerId) {
          // User had a delegation selected — validate it still exists
          const delegationExists = mapped.some(d => d.delegatorUserId === savedOwnerId);
          if (delegationExists) {
            setActiveCatalogOwnerId(savedOwnerId);
          } else if (nonOwnerWithDelegation) {
            // Stale saved delegator for a pure delegate — fall back to the
            // first available delegation, NOT the own sentinel (which would
            // strand a user who has no catalog of their own).
            selectFirstDelegation();
          } else {
            // Stale delegation for a catalog owner — fall back to own.
            localStorage.setItem(STORAGE_KEY, OWN_CATALOG_SENTINEL);
            setActiveCatalogOwnerId(null);
          }
        } else if (nonOwnerWithDelegation) {
          // First-ever visit, delegate-only user: auto-select first delegation.
          selectFirstDelegation();
        } else {
          // First-ever visit, catalog owner — write sentinel for future refreshes
          localStorage.setItem(STORAGE_KEY, OWN_CATALOG_SENTINEL);
        }

        isInitialized.current = true;
      }
    } catch (err) {
      logger.error('Failed to fetch delegations', err);
      setDelegatedCatalogs([]);
    } finally {
      setIsLoadingDelegations(false);
      setDelegationsFetched(true);
    }
  }, [isAuthenticated, user?.role]);

  // Fetch delegations on mount when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchDelegations();
    }
  }, [isAuthenticated, authLoading, fetchDelegations]);

  // PR 4b — fetch the user's owned catalogs (primary + add-ons) from
  // /api/subscription when the flag is on. Skipped when off so the existing
  // single-catalog flow doesn't pay the extra HTTP cost. Failures degrade
  // silently — the switcher will still render delegations + a "My Catalog"
  // option fed by the user's primary ALI ID from permissions.
  const fetchOwnedCatalogs = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    setIsLoadingOwnedCatalogs(true);
    // Reset each fetch — the fallback paths below can't tell expired from
    // free-account, so only the successful subscription path may set it.
    setAccountExpired(false);

    // Shared helper: persists the resolved owned-catalog list and runs the
    // first-load localStorage migration so both the subscription path and the
    // free-delegate fallback path stay consistent (DRY + immutable).
    const applyOwnedCatalogs = (list: OwnedCatalogInfo[]) => {
      setOwnedCatalogs(list);
      const savedAli = localStorage.getItem(ALI_STORAGE_KEY);
      if (savedAli === null && list.length > 0) {
        const legacyOwnerId = localStorage.getItem(STORAGE_KEY);
        if (legacyOwnerId === OWN_CATALOG_SENTINEL || legacyOwnerId === null) {
          setActiveCatalogAliId(list[0].catalogAliId);
          localStorage.setItem(ALI_STORAGE_KEY, list[0].catalogAliId);
        }
        // Delegator-userId case is handled by the delegation merge effect
        // below; we don't auto-pick a delegated catalog here.
      }
    };

    // When the subscription yields no primary (free/delegate account with no
    // Stripe subscription), synthesize the own primary catalog from
    // /api/user/ali-id — a user's ALI ID IS their primary catalog's
    // catalogAliId. Derive the display name from the auth user object the same
    // way UserProfile.getDisplayName() does.
    const buildFallbackFromAliId = async (): Promise<OwnedCatalogInfo[]> => {
      try {
        const aliResponse = await api.get('/api/user/ali-id');
        const aliId: string | undefined = aliResponse.data?.aliId;
        if (!aliId) {
          return [];
        }
        const firstName: string = user?.firstName ?? '';
        const lastName: string = user?.lastName ?? '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ');
        const creatorName =
          fullName.trim() ||
          (user as { username?: string } | null)?.username ||
          aliId;
        return [{ catalogAliId: aliId, creatorName, isPrimary: true }];
      } catch {
        // /api/user/ali-id also unavailable — leave ownedCatalogs empty.
        return [];
      }
    };

    try {
      const subscription = await getSubscription();
      // Defensive: SubscriptionState types catalogs as a required array, but
      // TypeScript types are runtime-unenforced at the network boundary. A
      // malformed server response that omits the primary catalog would
      // otherwise propagate `undefined` into ownedCatalogs and localStorage,
      // breaking the switcher in confusing ways. Treat the missing-primary
      // case as a free-delegate account and synthesize from /api/user/ali-id.
      const catalogs = subscription?.catalogs ?? [];
      const primary = catalogs.find((c) => c.isPrimary);
      if (!primary) {
        // Log a summary, not the raw subscription — the full object contains
        // Stripe subscription IDs, payment-method shapes, and discount codes
        // which shouldn't appear in browser console / log aggregators.
        logger.warn('fetchOwnedCatalogs: subscription missing primary; falling back to /api/user/ali-id', {
          catalogCount: catalogs.length,
          tier: subscription?.tier,
        });
        const fallback = await buildFallbackFromAliId();
        applyOwnedCatalogs(fallback);
        return;
      }
      // GH#1254: an expired catalog is fully hidden — even from its owner —
      // so it never reaches the switcher. The Billing page keeps seeing it
      // (own fetch via useSubscription), which is where Reactivate lives.
      const operational = catalogs.filter((c) => c.status !== 'expired');
      setAccountExpired(operational.length === 0);
      const merged: OwnedCatalogInfo[] = operational.map((c) => ({
        catalogAliId: c.catalogAliId,
        creatorName: c.creatorName,
        isPrimary: c.isPrimary,
      }));
      applyOwnedCatalogs(merged);
    } catch (err) {
      logger.error('Failed to fetch owned catalogs', err);
      // /api/subscription failed (e.g. 404 for a no-Stripe account). Attempt
      // to synthesize the own primary from /api/user/ali-id so free delegates
      // can still reach their own catalog.
      const fallback = await buildFallbackFromAliId();
      applyOwnedCatalogs(fallback);
    } finally {
      setIsLoadingOwnedCatalogs(false);
      setOwnedCatalogsFetched(true);
    }
    // Depend on the primitive display-name fields the fallback actually reads
    // (matching the sibling delegations effect's `user?.role` pattern) rather
    // than the whole `user` object — AuthContext rebuilds a fresh `user` on
    // every token refresh, which would otherwise re-fetch /api/subscription +
    // /api/user/ali-id needlessly.
  }, [isAuthenticated, user?.firstName, user?.lastName, user?.username]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchOwnedCatalogs();
    }
  }, [isAuthenticated, authLoading, fetchOwnedCatalogs]);

  // Clear state on logout
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ALI_STORAGE_KEY);
      setActiveCatalogOwnerId(null);
      setActiveCatalogAliId(null);
      setDelegatedCatalogs([]);
      setOwnedCatalogs([]);
      setAccountExpired(false);
      isInitialized.current = false;
      setDelegationsFetched(false);
      setOwnedCatalogsFetched(false);
    }
  }, [isAuthenticated, authLoading]);

  const switchCatalog = useCallback((ownerId: string | null) => {
    setActiveCatalogOwnerId(ownerId);
    localStorage.setItem(STORAGE_KEY, ownerId ?? OWN_CATALOG_SENTINEL);
  }, []);

  const switchToOwnCatalog = useCallback(() => {
    // Reset the ALI to the own primary too, not just the legacy owner id. Left
    // on a delegated ALI, the owner<->ALI reconciliation effect (which treats
    // the ALI as authoritative) would heal `activeCatalogOwnerId` straight back
    // to the delegator, defeating the escape-to-own-catalog the callers
    // ("My CV", own-object upload/edit) intend.
    setActiveCatalogAliId(null);
    localStorage.setItem(ALI_STORAGE_KEY, PRIMARY_SENTINEL);
    switchCatalog(null);
  }, [switchCatalog]);

  // PR 4b — unified switcher setter. Updates BOTH the new activeCatalogAliId
  // AND the legacy activeCatalogOwnerId so existing consumers keep working
  // when the selection lands on a delegated catalog. For owned catalogs
  // (primary or add-on) the legacy ownerId stays null since the data
  // belongs to the caller themselves.
  const switchToCatalogAliId = useCallback(
    (catalogAliId: string | null) => {
      setActiveCatalogAliId(catalogAliId);
      localStorage.setItem(ALI_STORAGE_KEY, catalogAliId ?? PRIMARY_SENTINEL);
      const delegationMatch = catalogAliId
        ? resolveBestDelegationByAliId(delegatedCatalogs, catalogAliId)
        : null;
      switchCatalog(delegationMatch?.delegatorUserId ?? null);
    },
    [delegatedCatalogs, switchCatalog]
  );

  // Resolve via the merge helper (not a bare first-match .find()) — the
  // delegator can have multiple rows (per-collection/per-catalog grain), and
  // consumers gate real edit permission off `activeDelegation.permissions`
  // (ObjectDetails, MyObjects), so a first-match `view` row would wrongly
  // downgrade a delegate who also holds a `full` row from another collection.
  // Memoized because the merge allocates a fresh object: downstream memos and
  // fetch effects (CurriculumVitae, DescribeTab) key on its identity, and an
  // unstable reference re-fires them on every provider render.
  const activeDelegation = useMemo(
    () =>
      activeCatalogOwnerId
        ? resolveBestDelegationByUserId(delegatedCatalogs, activeCatalogOwnerId)
        : null,
    [delegatedCatalogs, activeCatalogOwnerId]
  );

  // CV access is owner-scoped, not collection-scoped: aggregate canEditCv
  // across ALL of the active owner's delegation rows (per-collection grain
  // means there can be several, and they may disagree). A single
  // activeDelegation.find() could return a row with canEditCv=false while
  // another row for the same owner grants it.
  const activeOwnerCanEditCv = activeCatalogOwnerId
    ? delegatedCatalogs.some(d => {
        if (d.delegatorUserId !== activeCatalogOwnerId) return false;
        if (!d.canEditCv) return false;
        const expiresAt = Date.parse(d.expiresAt);
        return Number.isFinite(expiresAt) && expiresAt > Date.now();
      })
    : false;

  // True iff the caller has an active, non-expired delegation from this user.
  // Memoised against the delegation list so consumers passing it through
  // useMemo / Link `to=` props don't re-render when unrelated state churns.
  const isDelegateOf = useCallback(
    (delegatorUserId: string) => {
      const now = Date.now();
      return delegatedCatalogs.some(d => {
        if (d.delegatorUserId !== delegatorUserId) return false;
        const expiresAt = Date.parse(d.expiresAt);
        return Number.isFinite(expiresAt) && expiresAt > now;
      });
    },
    [delegatedCatalogs]
  );

  // PR 4b — derive the unified switcher list. When the flag is off, only
  // delegations populate it (matching the legacy switcher shape exactly).
  // When on, primary + add-ons + delegations are concatenated; the switcher
  // renders them as one flat list distinguished only by the ``kind`` badge.
  const availableCatalogs: AvailableCatalogInfo[] = (() => {
    const ownedRows: AvailableCatalogInfo[] = ownedCatalogs.map((c) => ({
      catalogAliId: c.catalogAliId,
      label: c.creatorName,
      kind: c.isPrimary ? 'own-primary' : 'own-addon',
      // ownerUserId is null for owned catalogs — the caller's own userId is
      // implicit, and consumers that need it should read it from useAuth().
      ownerUserId: null,
    }));
    // Dedupe to one switcher row per delegator ALI ID, preserving the order
    // each delegator first appears in. The delegations PK is per-collection
    // (migration 0007) and per-catalog (migration 0052) grain, so the same
    // delegator can legitimately produce multiple rows (e.g. two
    // album-scoped grants) — without this, GH#1251 renders exact duplicates.
    const seenDelegatorAliIds = new Set<string>();
    const orderedDelegatorAliIds: string[] = [];
    for (const d of delegatedCatalogs) {
      if (!seenDelegatorAliIds.has(d.delegatorAliId)) {
        seenDelegatorAliIds.add(d.delegatorAliId);
        orderedDelegatorAliIds.push(d.delegatorAliId);
      }
    }
    const delegatedRows: AvailableCatalogInfo[] = orderedDelegatorAliIds.map((aliId) => {
      const merged = resolveBestDelegationByAliId(delegatedCatalogs, aliId) as DelegatedCatalogInfo;
      return {
        catalogAliId: merged.delegatorAliId,
        label: merged.delegatorName ?? merged.delegatorAliId,
        kind: 'delegated',
        ownerUserId: merged.delegatorUserId,
        delegation: merged,
      };
    });
    return [...ownedRows, ...delegatedRows];
  })();

  // Catalogs the user may create/manage albums in. Owner-keyed: own catalogs
  // (primary + add-ons) collapse to one entry; each owner the user holds an
  // active, non-expired FULL delegation from appears once. Computed as a plain
  // const (not useMemo) so Date.now() is allowed here — same pattern as
  // activeOwnerCanEditCv above. Single source of truth shared by the Albums
  // page gate and the Create Album dialog so they can never disagree.
  const manageableCatalogs: ManageableCatalogInfo[] = computeManageableCatalogs(
    ownedCatalogs,
    delegatedCatalogs,
    Date.now(),
  );

  // Reconciliation effect — if `activeCatalogAliId` points at a catalog that
  // no longer appears in `availableCatalogs` (delegation revoked, add-on
  // removed, stale localStorage), drop it back to the first available row.
  // Gated on `*Fetched` flags (not just `!isLoading*`) because the loading
  // flags are false BOTH before the fetch starts AND after it completes —
  // running on the former would clobber the saved ALI before availableCatalogs
  // has been populated.
  useEffect(() => {
    if (!delegationsFetched || !ownedCatalogsFetched) return;
    if (activeCatalogAliId === null) return;
    const stillExists = availableCatalogs.some(
      (c) => c.catalogAliId === activeCatalogAliId
    );
    if (stillExists) {
      // The ALI is valid — heal a desync of the legacy `activeCatalogOwnerId`.
      // The two are stored separately and can diverge: a `switchToCatalogAliId`
      // call made before `delegatedCatalogs` loaded resolved no delegation match
      // and persisted owner='__own__' while the ALI pointed at a delegated
      // catalog. On reload the owner hydrated null (own) while the ALI said
      // delegated, so owner_id-scoped fetches returned the caller's OWN data
      // (a delegate's own albums leaking into the delegated catalog's views).
      //
      // Heal ONLY this forward direction (ALI is a delegated catalog whose
      // delegator the owner doesn't match). We intentionally do NOT force
      // owner=null when the ALI is an owned catalog: no production path sets the
      // owner independently of the ALI, so that reverse state can't occur, and
      // forcing it would fight the low-level `switchCatalog(ownerId)` setter.
      // Idempotent — a matching owner is a no-op.
      const match = delegatedCatalogs.find(
        (d) => d.delegatorAliId === activeCatalogAliId
      ) ?? null;
      if (match && activeCatalogOwnerId !== match.delegatorUserId) {
        switchCatalog(match.delegatorUserId);
      }
      return;
    }

    const fallback = availableCatalogs[0]?.catalogAliId ?? null;
    logger.warn(
      'ActiveCatalogContext: reconciling stale activeCatalogAliId',
      { stale: activeCatalogAliId, fallback, availableCount: availableCatalogs.length }
    );
    setActiveCatalogAliId(fallback);
    if (fallback === null) {
      localStorage.removeItem(ALI_STORAGE_KEY);
    } else {
      localStorage.setItem(ALI_STORAGE_KEY, fallback);
    }

    // The legacy ownerId may also be pointing at a delegator who no longer
    // delegates to us — keep it in sync with the new fallback.
    const fallbackDelegation = fallback
      ? delegatedCatalogs.find((d) => d.delegatorAliId === fallback) ?? null
      : null;
    switchCatalog(fallbackDelegation?.delegatorUserId ?? null);
  }, [
    activeCatalogAliId,
    activeCatalogOwnerId,
    availableCatalogs,
    delegatedCatalogs,
    delegationsFetched,
    ownedCatalogsFetched,
    switchCatalog,
  ]);

  const value: ActiveCatalogContextType = {
    activeCatalogOwnerId,
    activeDelegation,
    activeOwnerCanEditCv,
    delegatedCatalogs,
    isLoadingDelegations,
    delegationsFetched,
    isViewingDelegatedCatalog: activeCatalogOwnerId !== null,
    switchCatalog,
    switchToOwnCatalog,
    refreshDelegations: fetchDelegations,
    isDelegateOf,
    // PR 4b additions
    activeCatalogAliId,
    ownedCatalogs,
    availableCatalogs,
    isLoadingOwnedCatalogs,
    accountExpired,
    switchToCatalogAliId,
    manageableCatalogs,
  };

  return (
    <ActiveCatalogContext.Provider value={value}>
      {children}
    </ActiveCatalogContext.Provider>
  );
};
